import { OverlayModule } from '@angular/cdk/overlay';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { CommonModule, Location } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, Input, OnInit, PLATFORM_ID, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { LocaleSharedModule } from '@app/i18n/locale-shared.module';
import { MeetingsCardModule } from '@core/components/meeting-card/meetings-card.module';
import { MeetingCardHalfModule } from '@core/components/meeting-card-half/meeting-card-half.module';
import { MobileModalModule } from '@core/components/mobile-modal/mobile-modal.module';
import { ModalModule } from '@core/components/modal/modal.module';
import { ChangableComponent } from '@models/changable.component';
import { Store } from '@ngrx/store';
import { IconComponent } from '@shared/icon/icon.component';
import { CLibraryFilterData } from '@store/models';
import { getLibraryRouteData } from '@store/reducers/library.reducer';
import { Meeting, Router as ContentRouter } from 'lingo2-models';
import { DeviceDetectorService } from 'ngx-device-detector';
import { filter, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-meetings-group',
  standalone: true,
  imports: [
    LocaleSharedModule,
    CommonModule,
    OverlayModule,
    MeetingCardHalfModule,
    ModalModule,
    MeetingsCardModule,
    MobileModalModule,
    IconComponent,
  ],
  templateUrl: './meetings-group.component.html',
  styleUrls: ['./meetings-group.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MeetingsGroupComponent extends ChangableComponent implements OnInit {
  private platformId = inject(PLATFORM_ID);
  deviceService = inject(DeviceDetectorService);
  private location = inject(Location);
  private router = inject(Router);
  private store = inject(Store);

  @Input() public meetings: Meeting[];
  @ViewChild('scroller', { read: CdkVirtualScrollViewport }) public scroller: CdkVirtualScrollViewport;
  public isDragScrollEnabled: boolean;
  public itemSize = 415;
  public showModal = false;
  public openedCardIndex: number = null;
  public reachedPrev = false;
  public reachedNext = false;
  public item: Meeting;

  private meetingRoute: string[];
  private baseUrl: string;
  private libraryFilterData: CLibraryFilterData;

  public ngOnInit(): void {
    this.isDragScrollEnabled = this.deviceService.isDesktop();
    this.store
      .select(getLibraryRouteData)
      .pipe(
        filter((d) => !!d),
        takeUntil(this.destroyed$),
      )
      .subscribe({
        next: (libraryFilterData) => {
          this.libraryFilterData = libraryFilterData;
          this.baseUrl = libraryFilterData.url;
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  public moveLeft() {
    this.scroller.scrollToIndex(0, 'smooth');
  }

  public moveRight() {
    const offset = this.scroller.measureScrollOffset('left');
    this.scroller.scrollToOffset(offset + this.itemSize, 'smooth');
  }

  public togglePrev(event?) {
    if (this.openedCardIndex - 1 >= 0) {
      this.onOpenCard(this.openedCardIndex - 1);
      this.meetingRoute = ContentRouter.meetingRoute(this.item);
    }
    this.checkReached();
  }

  public toggleNext(event?) {
    if (this.openedCardIndex + 1 <= this.meetings.length - 1) {
      this.onOpenCard(this.openedCardIndex + 1);
      this.meetingRoute = ContentRouter.meetingRoute(this.item);
    }
    this.checkReached();
  }

  public checkReached() {
    const reachedPrev = this.openedCardIndex <= 0;
    const reachedNext = this.openedCardIndex >= this.meetings.length - 1;

    if (!reachedPrev && !reachedNext) {
      this.reachedPrev = this.reachedNext = false;
    } else if (reachedPrev && reachedNext) {
      this.reachedPrev = this.reachedNext = true;
    } else if (reachedPrev) {
      this.reachedPrev = true;
    } else if (reachedNext) {
      this.reachedNext = true;
    }
  }

  public onOpenCard(i) {
    // logger.log('MeetingsGroupComponent:onOpenCard', i);
    this.openedCardIndex = i;
    this.item = this.meetings[i];
    this.meetingRoute = ContentRouter.meetingRoute(this.item);
    if (this.baseUrl) {
      this.location.replaceState(this.meetingRoute.join(''));
    }
    this.checkReached();
    this.showModal = !!this.item;
  }

  public onCloseCard() {
    // logger.log('MeetingsGroupComponent:onCloseCard');
    this.openedCardIndex = null;
    this.item = null;
    if (this.baseUrl) {
      this.location.replaceState(this.baseUrl);
    }
    this.showModal = false;
  }

  public onCloseModal(event) {
    if (event) {
      this.onCloseCard();
    }
  }

  public trackByMeetingFn(index, item: Meeting): string {
    return item.id;
  }
}
